import React, { useState } from "react";
import { ChevronDown, ChevronUp, Phone, Mail, Clock, CreditCard, MapPin, Users } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqCategories = [
    {
      title: "Scheduling & Booking",
      icon: <Clock className="h-5 w-5 text-blue-600" />,
      questions: [
        {
          question: "How far in advance do I need to book transportation?",
          answer: "We prefer 24 hours advance notice for long-distance trips, but we're happy to work with last-minute requests whenever possible. For local trips, we can often accommodate same-day requests based on availability. For the best chance of getting your preferred time, we recommend booking as early as possible."
        },
        {
          question: "What are your business hours?",
          answer: "Our office hours are: Monday-Thursday 8:00 AM - 5:00 PM, Friday 8:00 AM - 4:00 PM, Saturday-Sunday 8:00 AM - 12:00 PM. However, we provide transportation services beyond these hours and can arrange early morning or evening trips when needed."
        },
        {
          question: "Can I schedule recurring appointments?",
          answer: "Absolutely! Many of our clients have recurring appointments for dialysis, physical therapy, or regular check-ups. We can set up a regular schedule and send reminders for each appointment. This also helps ensure your preferred time slots are reserved."
        },
        {
          question: "How will I be reminded about my appointment?",
          answer: "We offer several reminder options: phone calls, text messages (SMS), email, or a combination. You can choose your preferred method when booking. We typically send reminders 24 hours before your scheduled pickup time."
        },
        {
          question: "What if I need to cancel or reschedule?",
          answer: "Please contact us at least 2 hours before your scheduled pickup time for cancellations or changes. This helps us offer that time to other clients who may need it. Late cancellations may result in fees, but we understand emergencies happen and handle each situation individually."
        }
      ]
    },
    {
      title: "Insurance & Payment",
      icon: <CreditCard className="h-5 w-5 text-green-600" />,
      questions: [
        {
          question: "What insurance do you accept?",
          answer: "We accept most major insurance plans including Medicare, Medicaid, Blue Cross Blue Shield, Priority Health, Molina Healthcare, United Healthcare, Aetna, and Humana. We'll verify your coverage before your first trip and let you know what's covered under your plan."
        },
        {
          question: "Do you accept self-pay clients?",
          answer: "Yes! While we primarily work with insurance-covered trips, we welcome self-pay clients. We offer competitive rates and can provide cost estimates before your trip. Payment can be made by cash, check, or credit card."
        },
        {
          question: "Does my insurance require prior authorization?",
          answer: "Some insurance plans do require prior authorization for medical transportation. We'll check this when verifying your benefits and help obtain any necessary authorizations. It's best to contact us as early as possible if your plan requires prior auth."
        },
        {
          question: "What if my insurance doesn't cover the full cost?",
          answer: "If your insurance has a copay, deductible, or doesn't cover the full amount, you'll be responsible for the remaining balance. We'll let you know about any out-of-pocket costs before providing service so there are no surprises."
        },
        {
          question: "Do you help with insurance billing?",
          answer: "Yes, we handle all insurance billing for you. We submit claims directly to your insurance company and follow up on any issues. You'll receive explanation of benefits statements, and we'll work with you on any billing questions."
        }
      ]
    },
    {
      title: "Transportation Services",
      icon: <MapPin className="h-5 w-5 text-purple-600" />,
      questions: [
        {
          question: "What areas do you serve?",
          answer: "Our primary service area is Calhoun County including Battle Creek, Marshall, Albion, and Homer. We also provide service to surrounding counties and can arrange long-distance transportation to medical facilities throughout Michigan and neighboring states."
        },
        {
          question: "What's the difference between wheelchair accessible and ambulatory transportation?",
          answer: "Wheelchair accessible vehicles have lifts or ramps and can transport clients who remain in their wheelchairs during the trip. Ambulatory transportation is for clients who can walk and transfer to a regular vehicle seat, though they may use mobility aids like walkers or canes."
        },
        {
          question: "Can you transport medical equipment?",
          answer: "Yes, we can transport oxygen tanks, walkers, wheelchairs, and other medical equipment. Please let us know about any equipment when booking so we can ensure our vehicle has adequate space and any necessary accommodations."
        },
        {
          question: "Do you provide assistance getting in and out of vehicles?",
          answer: "Our drivers are trained to provide appropriate assistance with boarding and exiting vehicles. We can help with mobility aids, provide arm support, and ensure safe transfers. However, we cannot provide lifting or extensive physical assistance - that would require medical personnel."
        },
        {
          question: "What if I need to make stops during my trip?",
          answer: "We can accommodate multiple stops such as pharmacy pickups or brief errands related to your medical appointment. Additional stops may affect pricing and timing, so please mention them when booking. Our goal is to make your trip as convenient as possible."
        }
      ]
    },
    {
      title: "Safety & Requirements",
      icon: <Users className="h-5 w-5 text-red-600" />,
      questions: [
        {
          question: "Are your drivers trained and background checked?",
          answer: "Yes, all our drivers undergo comprehensive background checks, drug screening, and professional training in medical transportation, passenger assistance, and customer service. We also maintain ongoing training to ensure the highest standards of care."
        },
        {
          question: "What safety measures do you have in place?",
          answer: "Our vehicles are regularly inspected and maintained to DOT standards. We have wheelchair securement systems that meet federal requirements, comprehensive insurance coverage, and emergency procedures. All drivers are trained in safety protocols and emergency response."
        },
        {
          question: "Can a family member or caregiver ride with me?",
          answer: "Yes, in most cases we can accommodate one accompanying person at no additional charge, space permitting. Please let us know when booking if someone will be traveling with you so we can ensure adequate seating."
        },
        {
          question: "What if there's a medical emergency during transport?",
          answer: "Our drivers are trained in basic emergency response and will immediately call 911 if there's a medical emergency. We are not medical personnel and cannot provide medical care, but we'll ensure you get emergency medical attention as quickly as possible."
        },
        {
          question: "Do you transport clients with infectious diseases?",
          answer: "We can transport clients with certain controlled infectious conditions following appropriate safety protocols. This requires advance notice so we can prepare proper protective equipment. Please discuss specific medical conditions when booking."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our medical transportation services. 
            Can't find what you're looking for? Contact us directly for personalized assistance.
          </p>
        </div>

        {/* FAQ Categories */}
        {faqCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              {category.icon}
              <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
            </div>
            
            <div className="space-y-4">
              {category.questions.map((faq, faqIndex) => {
                const globalIndex = categoryIndex * 100 + faqIndex; // Unique index across all categories
                const isOpen = openItems.has(globalIndex);
                
                return (
                  <Card key={globalIndex} className="border-l-4 border-blue-500 hover:shadow-md transition-shadow duration-300">
                    <CardContent className="p-0">
                      <button
                        onClick={() => toggleItem(globalIndex)}
                        className="w-full p-6 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                        {isOpen ? (
                          <ChevronUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        )}
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-6">
                          <div className="border-t border-gray-200 pt-4">
                            <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {/* Still Have Questions */}
        <div className="mt-16 bg-blue-900 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
          <p className="text-blue-100 mb-6">
            Our friendly team is here to help with any questions about our services, 
            insurance coverage, or scheduling your transportation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600">
              <Phone className="h-4 w-4 mr-2" />
              <a href="tel:(269) 248-3514">Call (269) 248-3514</a>
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
              <Mail className="h-4 w-4 mr-2" />
              <a href="mailto:qualitytransportservices42@gmail.com">Send Email</a>
            </Button>
          </div>
          <div className="mt-6 text-sm text-blue-200">
            <Clock className="h-4 w-4 inline mr-1" />
            Office Hours: Mon-Thu 8am-5pm | Fri 8am-4pm | Sat-Sun 8am-12pm
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
            <CardContent>
              <h4 className="font-semibold text-gray-900 mb-2">Ready to Book?</h4>
              <p className="text-gray-600 text-sm mb-4">Schedule your transportation online</p>
              <Button asChild className="w-full bg-blue-900 hover:bg-blue-800">
                <a href="/book-ride">Book a Ride</a>
              </Button>
            </CardContent>
          </Card>
          
          <Card className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
            <CardContent>
              <h4 className="font-semibold text-gray-900 mb-2">Check Insurance</h4>
              <p className="text-gray-600 text-sm mb-4">Verify your coverage online</p>
              <Button asChild variant="outline" className="w-full border-blue-900 text-blue-900 hover:bg-blue-50">
                <a href="/insurance-verification">Verify Insurance</a>
              </Button>
            </CardContent>
          </Card>
          
          <Card className="text-center p-6 hover:shadow-lg transition-shadow duration-300">
            <CardContent>
              <h4 className="font-semibold text-gray-900 mb-2">Get a Quote</h4>
              <p className="text-gray-600 text-sm mb-4">Quick pricing estimate</p>
              <Button asChild variant="outline" className="w-full border-blue-900 text-blue-900 hover:bg-blue-50">
                <a href="/quick-quote">Get Quote</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FAQ;